import { StrategyDuration } from "@bridgesplit/abf-sdk";
import { getUnixTs, Result, TIME } from "@bridgesplit/utils";
import Decimal from "decimal.js";

import { AbfLoanExpanded, LendingVaultExpanded, LtvInfo, StrategyExpanded } from "../types";
import { getVaultNetAssetValue, getZcLedger } from "../api";

export function serializeStrategyDuration(strategyDuration: StrategyDuration) {
    const duration = strategyDuration.duration;
    const durationType = strategyDuration.durationType;
    return JSON.stringify({ duration, durationType });
}

export function deserializeStrategyDuration(serializedStrategyDuration: string): Result<StrategyDuration> {
    const strategyDuration = JSON.parse(serializedStrategyDuration) as StrategyDuration;
    if (strategyDuration.duration === undefined || strategyDuration.durationType === undefined) {
        return Result.errFromMessage("Invalid duration");
    }
    return Result.ok(strategyDuration);
}

export function deserializeStrategyDurationOrUndefined(
    serializedStrategyDuration: string
): StrategyDuration | undefined {
    return deserializeStrategyDuration(serializedStrategyDuration).unwrapOrUndefined();
}

export function isStrategyDurationSame(a: StrategyDuration | undefined, b: StrategyDuration | undefined) {
    if (!a || !b) return false;

    return serializeStrategyDuration(a) === serializeStrategyDuration(b);
}

// Borrow cap, pass in
export enum CapType {
    Supply,
    Borrow,
    Withdraw
}

interface MaxAmountFromCap {
    current1hrAmountRemaining: Decimal | null;
    current24hrAmountRemaining: Decimal | null;
    currentGlobalAmountRemaining: Decimal | null;
    maxBalanceRemaining: Decimal | null;
}

export function useVaultMaxAmountFromCap(
    vaultExpanded: LendingVaultExpanded | undefined,
    type: CapType
): MaxAmountFromCap | undefined {
    if (!vaultExpanded) {
        return undefined;
    }

    const { principalCaps, capMonitor } = (() => {
        switch (type) {
            case CapType.Supply:
                return {
                    principalCaps: vaultExpanded.vaultStrategy.terms.supplyCaps,
                    capMonitor: vaultExpanded.vaultStrategy.supplyCapMonitor
                };
            case CapType.Borrow:
                return {
                    principalCaps: vaultExpanded.vaultStrategy.terms.borrowCaps,
                    capMonitor: vaultExpanded.vaultStrategy.borrowCapMonitor
                };
            case CapType.Withdraw:
                return {
                    principalCaps: vaultExpanded.vaultStrategy.terms.withdrawCaps,
                    capMonitor: vaultExpanded.vaultStrategy.withdrawCapMonitor
                };
        }
    })();

    if (!principalCaps || !capMonitor) {
        return undefined;
    }

    const now = getUnixTs();
    let current1hrAmountRemaining: Decimal | null = null;
    let current24hrAmountRemaining: Decimal | null = null;
    let currentGlobalAmountRemaining: Decimal | null = null;

    // Calculate 1hr max
    const oneHourPeriodStart = capMonitor.startTime1hr.toNumber();
    const oneHourAmount = capMonitor.principal1hr?.toNumber();
    const oneHourCap = principalCaps.max1hr?.toNumber();

    if (oneHourCap !== undefined && oneHourAmount !== undefined) {
        if (now - oneHourPeriodStart > TIME.HOUR) {
            current1hrAmountRemaining = new Decimal(oneHourCap);
        } else {
            current1hrAmountRemaining = new Decimal(oneHourCap).minus(oneHourAmount);
        }
    }

    // Calculate 24hr max
    const twentyFourHourPeriodStart = capMonitor.startTime24hr.toNumber();
    const twentyFourHourAmount = capMonitor.principal24hr?.toNumber();
    const twentyFourHourCap = principalCaps.max24hr?.toNumber();

    if (twentyFourHourCap !== undefined && twentyFourHourAmount !== undefined) {
        if (now - twentyFourHourPeriodStart > TIME.DAY) {
            current24hrAmountRemaining = new Decimal(twentyFourHourCap);
        } else {
            current24hrAmountRemaining = new Decimal(twentyFourHourCap).minus(twentyFourHourAmount);
        }
    }

    // Calculate global max based on cap type
    const maxOutstanding = principalCaps.maxOutstanding?.toNumber();

    if (maxOutstanding !== undefined) {
        if (type === CapType.Supply) {
            currentGlobalAmountRemaining = Decimal.max(
                new Decimal(maxOutstanding).minus(getVaultNetAssetValue(vaultExpanded, now) ?? 0),
                new Decimal(0)
            );
        } else if (type === CapType.Borrow) {
            currentGlobalAmountRemaining = Decimal.max(
                new Decimal(maxOutstanding).minus(vaultExpanded.vaultStrategy.strategy.currentDeployedAmount),
                new Decimal(0)
            );
        } else {
            currentGlobalAmountRemaining = null;
        }
    }

    // Return minimum of all non-null caps
    const caps: Decimal[] = [];
    if (current1hrAmountRemaining !== null) {
        caps.push(current1hrAmountRemaining);
    }
    if (current24hrAmountRemaining !== null) {
        caps.push(current24hrAmountRemaining);
    }
    if (currentGlobalAmountRemaining !== null) {
        caps.push(currentGlobalAmountRemaining);
    }

    let maxBalanceRemaining: Decimal | null;
    if (caps.length === 0) {
        maxBalanceRemaining = null;
    } else {
        maxBalanceRemaining = Decimal.min(...caps);
    }

    return {
        current1hrAmountRemaining,
        current24hrAmountRemaining,
        currentGlobalAmountRemaining,
        maxBalanceRemaining
    };
}

export function findStrategyLtvInfo(
    strategy: StrategyExpanded | undefined,
    collateralMint: string | undefined
): LtvInfo | undefined {
    if (!strategy || !collateralMint) return undefined;

    const collateralTerms = strategy.collateral.find((c) => c.metadata.mint === collateralMint);

    return collateralTerms?.ltvInfo;
}

export function getTargetStrategyMintDurationTerms(
    strategy: StrategyExpanded | undefined,
    loanExpanded: AbfLoanExpanded | undefined,
    targetMint: string | undefined
) {
    const ledger = getZcLedger(loanExpanded);

    if (!strategy || !targetMint || !ledger) return undefined;

    const currentDuration: StrategyDuration = {
        duration: ledger.duration,
        durationType: ledger.durationType
    };

    const targetMintStrategyTerm = strategy.terms.assetTerms[targetMint];
    if (!targetMintStrategyTerm) return undefined;

    const matchingDurationTerms = targetMintStrategyTerm.durationAndApys.find(([duration]) =>
        isStrategyDurationSame(duration, currentDuration)
    );

    if (!matchingDurationTerms) return undefined;

    return matchingDurationTerms;
}

export function strategyIsTargetLoanCompatible(
    strategy: StrategyExpanded | undefined,
    loanExpanded: AbfLoanExpanded | undefined,
    targetMint: string | undefined,
    debtToRepay: number | undefined
): boolean {
    const ledger = getZcLedger(loanExpanded);
    if (!strategy || !ledger || !targetMint) return false;

    const newMintStrategyTerms = getTargetStrategyMintDurationTerms(strategy, loanExpanded, targetMint);

    if (!newMintStrategyTerms) return false;

    return strategy.totalBalance >= (debtToRepay ?? 0);
}
